import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  page: number
  totalPages: number
  total: number
  pageSize: number
  onChange: (next: number) => void
  darkMode: boolean
}

export function DrilldownPagination({ page, totalPages, total, pageSize, onChange, darkMode }: Props) {
  const { t } = useTranslation()
  if (totalPages <= 1) {
    return (
      <div className={cn('text-xs', darkMode ? 'text-slate-500' : 'text-slate-400')}>
        {t('manager.analytics.drilldown.totalCount', { count: total })}
      </div>
    )
  }
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const btnBase = cn(
    'inline-flex items-center justify-center h-8 w-8 rounded-md border',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:opacity-40 disabled:cursor-not-allowed',
    darkMode
      ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
  )
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={cn('text-xs', darkMode ? 'text-slate-400' : 'text-slate-500')}>
        {t('manager.analytics.drilldown.pageRange', { start, end, total })}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={btnBase}
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          aria-label={t('manager.analytics.drilldown.prevPage')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span
          className={cn(
            'px-2 text-sm tabular-nums',
            darkMode ? 'text-slate-300' : 'text-slate-600',
          )}
        >
          {page} / {totalPages}
        </span>
        <button
          type="button"
          className={btnBase}
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('manager.analytics.drilldown.nextPage')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
